import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import Icon from "@/components/common/Icon";
import { motion, useScroll, useTransform } from "framer-motion";
import SEO from "@/components/SEO";
import { Reveal, MaskLine, WindowGrid } from "@/components/motion/Reveal";
import { CTAButton, MoveCare, SectionLabel } from "@/components/common/Bits";
import BrandMarquee from "@/components/common/BrandMarquee";
import ServiceCard from "@/components/ui/ServiceCard";
import BeforeAfterSlider from "@/components/ui/BeforeAfterSlider";
import TestimonialCarousel from "@/components/sections/TestimonialCarousel";
import { waLink, WA_MESSAGES } from "@/data/company";
import { SERVICES, SPECIAL_SOLUTIONS, FEATURED_SLUGS } from "@/data/services";
import {
    JOURNEY_STEPS,
    WHO_WE_HELP,
    WHY_CHOOSE,
    HOW_IT_WORKS,
    BUNDLES,
} from "@/data/mockData";
import { usePosts, useProjects } from "@/hooks/useContent";

const trust = [
    "One-Stop Property Solutions",
    "Residential & Commercial",
    "Responsive Singapore Team",
    "Multiple Services, One Contact",
];

export default function Home() {
    const heroRef = useRef(null);
    const { data: projects } = useProjects();
    // The homepage teaser uses the first project that has a before/after pair.
    const showcase = projects.find((p) => p.before && p.after);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

    const featured = FEATURED_SLUGS.map((s) => SERVICES.find((x) => x.slug === s));
    const otherServices = SERVICES.filter((s) => !FEATURED_SLUGS.includes(s.slug));

    return (
        <>
            <SEO path="/" />

            {/* ================= HERO ================= */}
            <section ref={heroRef} className="relative overflow-hidden bg-white pt-28 sm:pt-32">
                <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 pb-20 lg:grid-cols-[1.05fr_0.95fr] lg:pb-28">
                    <div>
                        <Reveal>
                            <SectionLabel>Your one-stop property care partner</SectionLabel>
                        </Reveal>
                        <h1 className="mt-6 font-display text-[16vw] font-semibold leading-[0.92] tracking-tighter text-brand-blue sm:text-7xl lg:text-[6.2vw]">
                            <MaskLine delay={0.05}>Your <span className="text-brand-red">MOVE.</span></MaskLine>
                            <MaskLine delay={0.18}>Our <span className="text-brand-red">CARE.</span></MaskLine>
                        </h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5, duration: 0.7 }}
                            className="mt-7 max-w-xl text-base leading-relaxed text-brand-ink/70 sm:text-lg"
                        >
                            From moving and cleaning to painting, repairs and renovation,
                            ProCare.SG handles everything your property needs — before,
                            during and after every move.
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.65, duration: 0.7 }}
                            className="mt-8 flex flex-wrap gap-3"
                        >
                            <CTAButton href={waLink(WA_MESSAGES.quote)} whatsapp testid="hero-whatsapp-cta">
                                WhatsApp Us for a Free Quote
                            </CTAButton>
                            <CTAButton to="#services" variant="outline" onClick={(e) => {
                                e.preventDefault();
                                document.getElementById("featured-services")?.scrollIntoView({ behavior: "smooth" });
                            }} testid="hero-explore-cta">
                                Explore Our Services
                            </CTAButton>
                        </motion.div>
                        <motion.ul
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.85, duration: 0.7 }}
                            className="mt-10 grid grid-cols-2 gap-x-6 gap-y-3 sm:max-w-lg"
                        >
                            {trust.map((t) => (
                                <li key={t} className="flex items-center gap-2 text-sm text-brand-ink/70">
                                    <WindowGrid size={12} />
                                    {t}
                                </li>
                            ))}
                        </motion.ul>
                    </div>

                    {/* Hero image with parallax + clipped frame */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="relative"
                    >
                        <div className="relative overflow-hidden rounded-[28px] rounded-tr-[90px] shadow-brand-lg">
                            <motion.img
                                src="/images/hero-team.webp"
                                alt="ProCare.SG team preparing a Singapore condominium for move-in"
                                style={{ y: imgY, scale: imgScale }}
                                className="aspect-[4/5] w-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/25 to-transparent" />
                        </div>
                        <div className="absolute -bottom-6 -left-6 flex items-center gap-3 rounded-2xl bg-brand-blue px-5 py-4 text-white shadow-brand-lg">
                            <WindowGrid size={28} />
                            <div>
                                <p className="font-display text-lg font-semibold leading-none">One Team</p>
                                <p className="text-xs text-white/70">for every move</p>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* protecting-hand curve transition */}
                <div className="h-10 rounded-t-[60px] bg-brand-surface sm:h-16 sm:rounded-t-[120px]" />
            </section>

            {/* ================= MARQUEE ================= */}
            <BrandMarquee />

            {/* ================= JOURNEY ================= */}
            <section className="bg-brand-surface py-24">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal className="max-w-3xl">
                        <SectionLabel>The Move-to-Care Journey</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-brand-blue sm:text-4xl lg:text-5xl">
                            <MoveCare text="Every MOVE comes with a list. We take CARE of it." />
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-5">
                        {JOURNEY_STEPS.map((step, i) => (
                            <Reveal key={step.n} delay={i * 0.08}>
                                <div className="relative h-full rounded-3xl bg-white p-6 shadow-brand">
                                    <span className="font-display text-5xl font-bold text-brand-blue/10">{step.n}</span>
                                    <div className="mt-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-red/10 text-brand-red">
                                        <Icon name={step.icon} className="h-6 w-6" />
                                    </div>
                                    <h3 className="mt-4 font-display text-lg font-semibold text-brand-blue">{step.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-brand-ink/60">{step.desc}</p>
                                    {i < JOURNEY_STEPS.length - 1 && (
                                        <ArrowRight className="absolute -right-4 top-1/2 hidden h-6 w-6 -translate-y-1/2 text-brand-red/40 lg:block" />
                                    )}
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= INTRO ================= */}
            <section className="py-24">
                <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2">
                    <Reveal>
                        <div className="relative overflow-hidden rounded-[28px] rounded-bl-[80px] shadow-brand-lg">
                            <img src="/images/movein-prep.webp" alt="ProCare.SG team preparing a move-in ready property" className="aspect-[5/4] w-full object-cover" loading="lazy" />
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <SectionLabel>Complete property care</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-brand-blue sm:text-4xl">
                            Complete Property Care Under One Roof
                        </h2>
                        <p className="mt-6 text-base leading-relaxed text-brand-ink/70">
                            Moving into or handing over a property often involves coordinating
                            multiple contractors. ProCare.SG brings moving, cleaning, painting,
                            repairs, renovation and maintenance services together under one
                            reliable team. Whether you are a homeowner, tenant, landlord or
                            property agent, we help make every move smoother and less stressful.
                        </p>
                        <div className="mt-8">
                            <CTAButton to="/about-us" variant="blue" testid="intro-about-cta">
                                Learn More About ProCare.SG
                            </CTAButton>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ================= WHO WE HELP ================= */}
            <section className="bg-brand-surface py-24">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal className="max-w-2xl">
                        <SectionLabel>Who we help</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-brand-blue sm:text-4xl">
                            One team, every kind of move
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {WHO_WE_HELP.map((w, i) => (
                            <Reveal key={w.title} delay={i * 0.06}>
                                <div className="group h-full rounded-3xl border border-brand-blue/10 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-brand">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-blue text-white transition-colors group-hover:bg-brand-red">
                                        <Icon name={w.icon} className="h-6 w-6" />
                                    </div>
                                    <h3 className="mt-5 font-display text-xl font-semibold text-brand-blue">{w.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-brand-ink/60">{w.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= FEATURED SERVICES ================= */}
            <section id="featured-services" className="py-24">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal className="max-w-2xl">
                        <SectionLabel>Featured services</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-brand-blue sm:text-4xl">
                            We <span className="text-brand-red">Move.</span> We Clean. We Paint.
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid gap-6 md:grid-cols-3">
                        {featured.map((s, i) => (
                            <Reveal key={s.slug} delay={i * 0.1}>
                                <ServiceCard service={s} />
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= OTHER SERVICES ================= */}
            <section className="bg-brand-surface py-24">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal className="max-w-2xl">
                        <SectionLabel>More property services</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-brand-blue sm:text-4xl">
                            Everything your property needs
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {otherServices.map((s, i) => (
                            <Reveal key={s.slug} delay={i * 0.05}>
                                <Link
                                    to={`/services/${s.slug}`}
                                    data-testid={`other-service-${s.slug}`}
                                    className="group flex h-full flex-col rounded-3xl border border-brand-blue/10 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand-red/30 hover:shadow-brand"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-blue/5 text-brand-blue">
                                            <Icon name={s.icon} className="h-6 w-6" />
                                        </div>
                                        <ArrowUpRight className="h-5 w-5 text-brand-red opacity-0 transition-opacity group-hover:opacity-100" />
                                    </div>
                                    <h3 className="mt-5 font-display text-xl font-semibold text-brand-blue">{s.name}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-brand-ink/60">{s.short}</p>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= SPECIAL SOLUTIONS ================= */}
            <section className="relative overflow-hidden bg-brand-blue py-24 text-white grain">
                <div className="pointer-events-none absolute -right-16 -top-16 opacity-10">
                    <WindowGrid size={280} />
                </div>
                <div className="relative mx-auto max-w-7xl px-6">
                    <Reveal className="max-w-2xl">
                        <SectionLabel light>Other solutions</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                            Specialist <span className="text-brand-red">CARE</span> beyond everyday works
                        </h2>
                        <p className="mt-4 text-white/70">
                            From spalling concrete and mould treatment to full events set-up,
                            we handle the jobs that need a specialist team.
                        </p>
                    </Reveal>
                    <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {SPECIAL_SOLUTIONS.map((s, i) => (
                            <Reveal key={s.slug} delay={i * 0.1}>
                                <Link
                                    to={`/special-solutions/${s.slug}`}
                                    data-testid={`special-${s.slug}`}
                                    className="group relative flex h-full flex-col overflow-hidden rounded-[28px] rounded-bl-[70px] bg-white/5 p-8 ring-1 ring-white/10 backdrop-blur transition-all duration-300 hover:bg-white/10"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-red text-white">
                                            <Icon name={s.icon} className="h-7 w-7" />
                                        </div>
                                        <h3 className="font-display text-2xl font-semibold">{s.name}</h3>
                                    </div>
                                    <p className="mt-5 text-sm leading-relaxed text-white/70">{s.intro}</p>
                                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand-red">
                                        {s.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                    </span>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= WHY CHOOSE US ================= */}
            <section className="py-24">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal className="max-w-2xl">
                        <SectionLabel>Why ProCare.SG</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-brand-blue sm:text-4xl">
                            Why trust ProCare.SG with your property?
                        </h2>
                        <p className="mt-4 text-brand-ink/70">
                            Instead of managing several contractors, work with one team that
                            understands the full scope of your move.
                        </p>
                    </Reveal>
                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {WHY_CHOOSE.map((w, i) => (
                            <Reveal key={w.title} delay={i * 0.05}>
                                <div className="flex h-full gap-4 rounded-3xl bg-brand-surface p-6">
                                    <WindowGrid size={22} className="mt-1 shrink-0" />
                                    <div>
                                        <h3 className="font-display text-lg font-semibold text-brand-blue">{w.title}</h3>
                                        <p className="mt-1.5 text-sm leading-relaxed text-brand-ink/60">{w.desc}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= HOW IT WORKS ================= */}
            <section className="bg-brand-surface py-24">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal className="max-w-2xl">
                        <SectionLabel>How it works</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-brand-blue sm:text-4xl">
                            Simple, from first message to finished job
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                        {HOW_IT_WORKS.map((h, i) => (
                            <Reveal key={h.n} delay={i * 0.08}>
                                <div className="relative h-full rounded-3xl bg-white p-7 shadow-brand">
                                    <span className="font-display text-6xl font-bold text-brand-blue/10">{h.n}</span>
                                    <h3 className="mt-3 font-display text-xl font-semibold text-brand-blue">{h.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-brand-ink/60">{h.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= BUNDLES ================= */}
            <section className="py-24">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal className="max-w-2xl">
                        <SectionLabel>Service bundles</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-brand-blue sm:text-4xl">
                            Bundles built around your move
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                        {BUNDLES.map((b, i) => (
                            <Reveal key={b.slug} delay={i * 0.08}>
                                <div className={`relative flex h-full flex-col rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1 ${b.popular ? "bg-brand-blue text-white shadow-brand-lg ring-2 ring-brand-red" : "border border-brand-blue/10 bg-white shadow-brand"}`}>
                                    {b.popular && (
                                        <span className="absolute -top-3 left-7 rounded-full bg-brand-red px-3 py-1 text-xs font-bold uppercase tracking-widest text-white">Popular</span>
                                    )}
                                    <h3 className={`font-display text-xl font-semibold ${b.popular ? "text-white" : "text-brand-blue"}`}>{b.name}</h3>
                                    <p className={`mt-2 text-sm ${b.popular ? "text-white/70" : "text-brand-ink/60"}`}>{b.desc}</p>
                                    <ul className={`mt-5 flex-1 space-y-2 text-sm ${b.popular ? "text-white/80" : "text-brand-ink/70"}`}>
                                        {b.items.map((it) => (
                                            <li key={it} className="flex items-center gap-2">
                                                <Check className={`h-4 w-4 ${b.popular ? "text-brand-red" : "text-brand-red"}`} />
                                                {it}
                                            </li>
                                        ))}
                                    </ul>
                                    <a
                                        href={waLink(`Hi ProCare.SG, I'd like to customise the "${b.name}" package.`)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`bundle-cta-${b.slug}`}
                                        className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${b.popular ? "bg-brand-red text-white hover:bg-brand-red/90" : "bg-brand-blue text-white hover:bg-brand-deep"}`}
                                    >
                                        Customise This Package
                                    </a>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= OUR WORKS PREVIEW ================= */}
            <section className="bg-brand-surface py-24">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal className="flex flex-wrap items-end justify-between gap-4">
                        <div className="max-w-2xl">
                            <SectionLabel>Our works</SectionLabel>
                            <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-brand-blue sm:text-4xl">
                                Recent property transformations
                            </h2>
                        </div>
                        <CTAButton to="/our-works" variant="outline" testid="works-viewmore-cta">View More of Our Works</CTAButton>
                    </Reveal>
                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {projects.slice(0, 6).map((p, i) => (
                            <Reveal key={p.id} delay={i * 0.06}>
                                <Link to={`/our-works/${p.slug}`} data-testid={`project-preview-${p.slug}`} className="group block overflow-hidden rounded-3xl bg-white shadow-brand transition-all duration-300 hover:-translate-y-1 hover:shadow-brand-lg">
                                    <div className="relative aspect-[4/3] overflow-hidden">
                                        <img src={p.featuredImage} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                        <span className="absolute left-4 top-4 rounded-full bg-brand-red px-3 py-1 text-xs font-bold uppercase tracking-widest text-white">{p.category}</span>
                                    </div>
                                    <div className="p-5">
                                        <h3 className="font-display text-lg font-semibold text-brand-blue">{p.title}</h3>
                                        <p className="mt-1 text-xs uppercase tracking-widest text-brand-ink/65">{p.propertyType} · {p.location}</p>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= BEFORE / AFTER ================= */}
            {showcase && (
            <section className="py-24">
                <div className="mx-auto max-w-5xl px-6">
                    <Reveal className="mx-auto max-w-2xl text-center">
                        <SectionLabel className="justify-center">Before & After</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-brand-blue sm:text-4xl">
                            See the <span className="text-brand-red">CARE</span> for yourself
                        </h2>
                        <p className="mt-4 text-brand-ink/70">Drag the slider to reveal the transformation.</p>
                    </Reveal>
                    <Reveal delay={0.1} className="mt-12">
                        <BeforeAfterSlider before={showcase.before} after={showcase.after} />
                    </Reveal>
                </div>
            </section>
            )}

            {/* ================= TESTIMONIALS ================= */}
            <section className="bg-brand-surface py-24">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal className="max-w-2xl">
                        <SectionLabel>Testimonials</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-brand-blue sm:text-4xl">
                            Trusted across every kind of move
                        </h2>
                    </Reveal>
                    <div className="mt-12">
                        <TestimonialCarousel />
                    </div>
                </div>
            </section>

            {/* ================= BLOG PREVIEW ================= */}
            <BlogPreview />

            {/* ================= FINAL CTA ================= */}
            <section className="relative overflow-hidden bg-brand-blue py-24 text-white grain">
                <div className="pointer-events-none absolute -left-20 bottom-0 opacity-10">
                    <WindowGrid size={300} />
                </div>
                <div className="relative mx-auto max-w-4xl px-6 text-center">
                    <h2 className="font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
                        <MoveCare text="Planning a MOVE? Let ProCare.SG take CARE of the rest." />
                    </h2>
                    <p className="mx-auto mt-6 max-w-2xl text-white/70">
                        Tell us what your property needs and our team will help coordinate the right services.
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <CTAButton href={waLink(WA_MESSAGES.quote)} whatsapp testid="final-whatsapp-cta">WhatsApp Us Now</CTAButton>
                        <CTAButton to="/contact" variant="light" testid="final-quote-cta">Request a Quotation</CTAButton>
                    </div>
                </div>
            </section>
        </>
    );
}

function BlogPreview() {
    const { data: allPosts } = usePosts();
    const posts = allPosts.slice(0, 3);
    if (!posts.length) return null;
    return (
        <section className="py-24">
            <div className="mx-auto max-w-7xl px-6">
                <Reveal className="flex flex-wrap items-end justify-between gap-4">
                    <div className="max-w-2xl">
                        <SectionLabel>From the blog</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-brand-blue sm:text-4xl">
                            Property care tips for every move
                        </h2>
                    </div>
                    <CTAButton to="/blog" variant="outline" testid="blog-viewall-cta">Read the Blog</CTAButton>
                </Reveal>
                <div className="mt-12 grid gap-6 md:grid-cols-3">
                    {posts.map((p, i) => (
                        <Reveal key={p.id} delay={i * 0.08}>
                            <Link to={`/blog/${p.slug}`} data-testid={`blog-preview-${p.slug}`} className="group flex h-full flex-col overflow-hidden rounded-3xl border border-brand-blue/10 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-brand">
                                <div className="aspect-[16/10] overflow-hidden">
                                    <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                </div>
                                <div className="flex flex-1 flex-col p-6">
                                    <span className="text-xs font-bold uppercase tracking-widest text-brand-red">{p.category}</span>
                                    <h3 className="mt-2 font-display text-lg font-semibold leading-snug text-brand-blue">{p.title}</h3>
                                    <p className="mt-2 flex-1 text-sm leading-relaxed text-brand-ink/60">{p.excerpt}</p>
                                    <span className="mt-4 text-xs text-brand-ink/60">{new Date(p.date).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" })}</span>
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
